// ../../packages/auth-client/src/constants.ts
var LOGIN_URL = "https://login.galigali.in";
var STORAGE_KEY_ACCESS = "gg_access_token";
var STORAGE_KEY_REFRESH = "gg_refresh_token";
var STORAGE_KEY_EXPIRY = "gg_token_expiry";
var STORAGE_KEY_USER = "gg_user";
var STORAGE_KEY_TENANTS = "gg_tenants";
var STORAGE_KEY_ACTIVE_TENANT = "gg_active_tenant_id";
var AUTH_CHANNEL_NAME = "gg_auth";
var REFRESH_LOCK_KEY = "gg_refresh_lock";
var REFRESH_LOCK_TTL_MS = 1e4;
var REFRESH_BUFFER_MS = 5 * 60 * 1e3;

// ../../packages/auth-client/src/storage.ts
function storageGet(key) {
  if (typeof window === "undefined") return null;
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}
function storageSet(key, value) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(key, value);
  } catch {
  }
}
function storageRemove(key) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(key);
  } catch {
  }
}

// ../../packages/auth-client/src/jwt.ts
function base64UrlDecode(input) {
  const padded = input.replace(/-/g, "+").replace(/_/g, "/");
  const pad = padded.length % 4;
  const fullyPadded = pad ? padded + "=".repeat(4 - pad) : padded;
  if (typeof atob !== "undefined") return atob(fullyPadded);
  return Buffer.from(fullyPadded, "base64").toString("binary");
}
function parseJwtPayload(token) {
  try {
    const parts = token.split(".");
    if (parts.length < 2 || !parts[1]) return null;
    const json = base64UrlDecode(parts[1]);
    return JSON.parse(json);
  } catch {
    return null;
  }
}
function isTokenExpired(token) {
  if (!token) return true;
  const payload = parseJwtPayload(token);
  if (!payload || typeof payload.exp !== "number") return true;
  return payload.exp * 1e3 - Date.now() < REFRESH_BUFFER_MS;
}
function tokenExpiryMs(token) {
  const payload = parseJwtPayload(token);
  if (!payload || typeof payload.exp !== "number") return null;
  return payload.exp * 1e3;
}

// ../../packages/auth-client/src/fragment.ts
function consumeFragment() {
  if (typeof window === "undefined") return { tokens: null, error: null };
  const hash = window.location.hash || "";
  if (!hash || hash.length < 2) return { tokens: null, error: null };
  const params = new URLSearchParams(hash.slice(1));
  if (window.history && typeof window.history.replaceState === "function") {
    const cleanUrl = window.location.pathname + window.location.search;
    try {
      window.history.replaceState(null, "", cleanUrl);
    } catch {
    }
  }
  const errorCode = params.get("error");
  if (errorCode) {
    return {
      tokens: null,
      error: {
        error: errorCode,
        errorDescription: params.get("error_description")
      }
    };
  }
  const accessToken = params.get("access_token");
  if (!accessToken) return { tokens: null, error: null };
  return {
    tokens: {
      accessToken,
      refreshToken: params.get("refresh_token"),
      expiresIn: parseInt(params.get("expires_in") || "3600", 10) || 3600
    },
    error: null
  };
}

// ../../packages/auth-client/src/broadcast.ts
var channel = null;
var listeners = /* @__PURE__ */ new Set();
function getChannel() {
  if (typeof window === "undefined") return null;
  if (typeof BroadcastChannel === "undefined") return null;
  if (!channel) {
    channel = new BroadcastChannel(AUTH_CHANNEL_NAME);
    channel.onmessage = (ev) => {
      const msg = ev.data;
      if (!msg || typeof msg.type !== "string") return;
      for (const l of listeners) {
        try {
          l(msg);
        } catch {
        }
      }
    };
  }
  return channel;
}
function broadcast(msg) {
  const ch = getChannel();
  if (ch) ch.postMessage(msg);
}
function onBroadcast(l) {
  listeners.add(l);
  getChannel();
  return () => {
    listeners.delete(l);
  };
}

// ../../packages/auth-client/src/refresh.ts
var inFlight = null;
var crossTabResolvers = [];
function notifyCrossTabRefreshed(accessToken, error) {
  while (crossTabResolvers.length) {
    const r = crossTabResolvers.shift();
    if (error) r.reject(new Error("Cross-tab refresh failed"));
    else r.resolve(accessToken);
  }
}
function isRefreshLocked() {
  const v = storageGet(REFRESH_LOCK_KEY);
  if (!v) return false;
  return Date.now() - parseInt(v, 10) < REFRESH_LOCK_TTL_MS;
}
function acquireRefreshLock() {
  storageSet(REFRESH_LOCK_KEY, String(Date.now()));
}
function releaseRefreshLock() {
  storageRemove(REFRESH_LOCK_KEY);
}
function waitForCrossTabRefresh() {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      reject(new Error("Cross-tab refresh timeout"));
    }, REFRESH_LOCK_TTL_MS);
    crossTabResolvers.push({
      resolve: (t) => {
        clearTimeout(timer);
        resolve(t);
      },
      reject: (e) => {
        clearTimeout(timer);
        reject(e);
      }
    });
  });
}
async function refreshAccessToken(deps) {
  if (inFlight) return inFlight;
  const refreshToken = storageGet(STORAGE_KEY_REFRESH);
  if (!refreshToken) {
    deps.onSessionEnded();
    return null;
  }
  if (isRefreshLocked()) {
    try {
      return await waitForCrossTabRefresh();
    } catch {
      const stored = storageGet(STORAGE_KEY_ACCESS);
      if (stored && !isTokenExpired(stored)) {
        deps.scheduleNextRefresh(stored);
        return stored;
      }
    }
  }
  inFlight = (async () => {
    acquireRefreshLock();
    try {
      const url = (deps.loginUrl || LOGIN_URL) + "/auth/refresh";
      const res = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refreshToken })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        const code = data.code || "";
        if (code === "REUSE_DETECTED" || code === "TOKEN_EXPIRED" || code === "INVALID_TOKEN") {
          deps.clearTokens();
          broadcast({ type: "token_refresh_failed" });
          deps.onSessionEnded();
          return null;
        }
        throw new Error(data.error || `refresh_failed_${res.status}`);
      }
      if (!data.accessToken) {
        throw new Error("refresh_response_missing_access_token");
      }
      deps.storeTokens(data.accessToken, data.refreshToken ?? refreshToken);
      deps.scheduleNextRefresh(data.accessToken);
      broadcast({
        type: "token_refreshed",
        accessToken: data.accessToken,
        refreshToken: data.refreshToken ?? null
      });
      return data.accessToken;
    } catch (err) {
      broadcast({ type: "token_refresh_failed" });
      throw err;
    } finally {
      releaseRefreshLock();
      inFlight = null;
    }
  })();
  return inFlight;
}

// ../../packages/auth-client/src/index.ts
var initOptions = {
  origin: "galigali-in",
  loginUrl: LOGIN_URL,
  requireAuth: true
};
var refreshTimer = null;
var eventListeners = /* @__PURE__ */ new Set();
function emit(e) {
  for (const l of eventListeners) {
    try {
      l(e);
    } catch {
    }
  }
}
function storeTokens(accessToken, refreshToken) {
  storageSet(STORAGE_KEY_ACCESS, accessToken);
  if (refreshToken) storageSet(STORAGE_KEY_REFRESH, refreshToken);
  const payload = parseJwtPayload(accessToken);
  if (payload && typeof payload.exp === "number") {
    storageSet(STORAGE_KEY_EXPIRY, String(payload.exp * 1e3));
    const user = {
      id: payload.sub || "",
      email: payload.email || null,
      phone: payload.phone || null,
      name: payload.name || null,
      isSuperAdmin: !!payload.is_super_admin,
      personalTenantId: payload.personal_tenant_id || null
    };
    storageSet(STORAGE_KEY_USER, JSON.stringify(user));
    if (Array.isArray(payload.tenants)) {
      storageSet(STORAGE_KEY_TENANTS, JSON.stringify(payload.tenants));
    }
    if (!storageGet(STORAGE_KEY_ACTIVE_TENANT) && payload.active_tenant_id) {
      storageSet(STORAGE_KEY_ACTIVE_TENANT, payload.active_tenant_id);
    }
  }
}
function clearTokens() {
  for (const k of [
    STORAGE_KEY_ACCESS,
    STORAGE_KEY_REFRESH,
    STORAGE_KEY_EXPIRY,
    STORAGE_KEY_USER,
    STORAGE_KEY_TENANTS,
    STORAGE_KEY_ACTIVE_TENANT
  ]) storageRemove(k);
  if (refreshTimer) {
    clearTimeout(refreshTimer);
    refreshTimer = null;
  }
}
var TRANSIENT_RETRY_DELAYS_MS = [500, 1500, 4e3];
var RETRY_WHILE_VALID_MS = 3e4;
function delay(ms) {
  return new Promise((r) => setTimeout(r, ms));
}
function scheduleNextRefresh(accessToken) {
  if (refreshTimer) {
    clearTimeout(refreshTimer);
    refreshTimer = null;
  }
  const payload = parseJwtPayload(accessToken);
  if (!payload || typeof payload.exp !== "number") return;
  const msUntilExpiry = payload.exp * 1e3 - Date.now();
  const msUntilRefresh = Math.max(msUntilExpiry - REFRESH_BUFFER_MS, 1e4);
  refreshTimer = setTimeout(() => {
    void doRefresh();
  }, msUntilRefresh);
}
var offlineRetryArmed = false;
var offlineHoldUntil = 0;
function armOfflineRetry() {
  if (offlineRetryArmed || typeof window === "undefined") return;
  offlineRetryArmed = true;
  const retry = () => {
    window.removeEventListener("online", retry);
    if (!offlineRetryArmed) return;
    offlineRetryArmed = false;
    void doRefresh();
  };
  window.addEventListener("online", retry);
  setTimeout(retry, 6e4);
}
function scheduleRetryWhileValid() {
  if (refreshTimer) {
    clearTimeout(refreshTimer);
    refreshTimer = null;
  }
  const stored = storageGet(STORAGE_KEY_ACCESS);
  const expMs = stored ? tokenExpiryMs(stored) : null;
  if (expMs == null) return;
  const msLeft = expMs - Date.now();
  if (msLeft <= 0) return;
  const wait = Math.min(RETRY_WHILE_VALID_MS, Math.max(msLeft - 2e3, 1e3));
  refreshTimer = setTimeout(() => {
    void doRefresh();
  }, wait);
}
async function doRefresh() {
  if (typeof navigator !== "undefined" && navigator.onLine === false && Date.now() < offlineHoldUntil) {
    return storageGet(STORAGE_KEY_ACCESS);
  }
  if (sameSiteAsLogin()) {
    const r = await reconcileIdentity();
    if (r === "adopted") return storageGet(STORAGE_KEY_ACCESS);
    if (r === "ended") {
      if (initOptions.requireAuth) redirectToLogin();
      return null;
    }
  }
  for (let attempt = 0; ; attempt++) {
    try {
      const newToken = await refreshAccessToken({
        loginUrl: initOptions.loginUrl,
        storeTokens: (a, r) => storeTokens(a, r),
        clearTokens,
        scheduleNextRefresh,
        onSessionEnded: () => {
          if (initOptions.requireAuth) redirectToLogin();
        }
      });
      if (newToken) emit({ type: "refresh", accessToken: newToken });
      return newToken;
    } catch (err) {
      if (attempt < TRANSIENT_RETRY_DELAYS_MS.length) {
        await delay(TRANSIENT_RETRY_DELAYS_MS[attempt]);
        continue;
      }
      emit({ type: "refresh_failed", reason: err.message });
      const stored = storageGet(STORAGE_KEY_ACCESS);
      const expMs = stored ? tokenExpiryMs(stored) : null;
      if (stored && expMs != null && expMs - Date.now() > 5e3) {
        scheduleRetryWhileValid();
        return stored;
      }
      const netError = err instanceof TypeError && /fetch|network|load failed|connection/i.test(err.message || "");
      const offline = typeof navigator !== "undefined" && navigator.onLine === false || netError;
      if (offline) {
        offlineHoldUntil = Date.now() + 15e3;
        armOfflineRetry();
        return stored ?? null;
      }
      if (initOptions.requireAuth) redirectToLogin();
      return null;
    }
  }
}
function buildLoginUrl() {
  if (typeof window === "undefined") return initOptions.loginUrl;
  const returnUrl = window.location.href;
  const u = new URL(initOptions.loginUrl + "/");
  u.searchParams.set("origin", initOptions.origin);
  u.searchParams.set("return_url", returnUrl);
  return u.toString();
}
function redirectToLogin() {
  if (typeof window === "undefined") return;
  window.location.href = buildLoginUrl();
}
function registrableDomain(host) {
  const hostname = host.toLowerCase().split(":")[0] || host.toLowerCase();
  return hostname.split(".").slice(-2).join(".");
}
function sameSiteAsLogin() {
  if (typeof window === "undefined") return false;
  try {
    return registrableDomain(window.location.host) === registrableDomain(new URL(initOptions.loginUrl).host);
  } catch {
    return false;
  }
}
function reconcileFetch(path) {
  const ac = new AbortController();
  const t = setTimeout(() => ac.abort(), 2500);
  return fetch(initOptions.loginUrl + path, { credentials: "include", signal: ac.signal }).finally(() => clearTimeout(t));
}
var reconcileInFlight = null;
function reconcileIdentity() {
  if (reconcileInFlight) return reconcileInFlight;
  reconcileInFlight = reconcileOnce().finally(() => {
    reconcileInFlight = null;
  });
  return reconcileInFlight;
}
async function reconcileOnce() {
  if (!sameSiteAsLogin()) return "skip";
  let res;
  try {
    res = await reconcileFetch("/auth/whoami");
  } catch {
    return "skip";
  }
  if (res.status === 401) {
    clearTokens();
    return "ended";
  }
  if (!res.ok) {
    return "skip";
  }
  let cookieUserId;
  try {
    const data = await res.json();
    cookieUserId = data.userId;
  } catch {
    return "skip";
  }
  if (!cookieUserId) return "skip";
  const storedToken = storageGet(STORAGE_KEY_ACCESS);
  const storedSub = storedToken ? parseJwtPayload(storedToken)?.sub : void 0;
  if (!storedSub || storedSub === cookieUserId) return "ok";
  let sdata;
  try {
    const sres = await reconcileFetch("/auth/adopt");
    if (!sres.ok) return "skip";
    sdata = await sres.json();
  } catch {
    return "skip";
  }
  if (!sdata.accessToken) return "skip";
  clearTokens();
  storeTokens(sdata.accessToken, sdata.refreshToken);
  scheduleNextRefresh(sdata.accessToken);
  emit({ type: "login", accessToken: sdata.accessToken });
  broadcast({ type: "token_refreshed", accessToken: sdata.accessToken, refreshToken: sdata.refreshToken ?? null });
  return "adopted";
}
var unsubscribeBroadcast = null;
var visibilityAttached = false;
function attachVisibilityRefresh() {
  if (visibilityAttached || typeof document === "undefined") return;
  visibilityAttached = true;
  const maybeRefresh = () => {
    if (document.visibilityState === "hidden") return;
    const refreshIfExpired = () => {
      if (!storageGet(STORAGE_KEY_REFRESH)) return;
      const stored = storageGet(STORAGE_KEY_ACCESS);
      if (!stored || isTokenExpired(stored)) void doRefresh();
    };
    if (sameSiteAsLogin()) {
      void reconcileIdentity().then((r) => {
        if (r === "ended") {
          if (initOptions.requireAuth) redirectToLogin();
          return;
        }
        refreshIfExpired();
      }, refreshIfExpired);
    } else {
      refreshIfExpired();
    }
  };
  document.addEventListener("visibilitychange", maybeRefresh);
  if (typeof window !== "undefined") window.addEventListener("focus", maybeRefresh);
}
function attachBroadcastListener() {
  if (unsubscribeBroadcast) return;
  unsubscribeBroadcast = onBroadcast((msg) => {
    if (msg.type === "token_refreshed") {
      storeTokens(msg.accessToken, msg.refreshToken);
      scheduleNextRefresh(msg.accessToken);
      notifyCrossTabRefreshed(msg.accessToken);
      emit({ type: "refresh", accessToken: msg.accessToken });
    } else if (msg.type === "token_refresh_failed") {
      notifyCrossTabRefreshed(null, true);
    } else if (msg.type === "logout") {
      clearTokens();
      emit({ type: "logout" });
      redirectToLogin();
    }
  });
}
var authClient = {
  /**
   * Initialize the client. Idempotent — safe to call multiple times.
   * Returns true if a usable session is in place at return time.
   */
  async init(opts) {
    initOptions = {
      origin: opts.origin,
      loginUrl: opts.loginUrl || LOGIN_URL,
      requireAuth: opts.requireAuth !== false
    };
    if (typeof window === "undefined") return false;
    attachBroadcastListener();
    attachVisibilityRefresh();
    const frag = consumeFragment();
    if (frag.error) {
      emit({ type: "refresh_failed", reason: frag.error.error });
    }
    if (frag.tokens) {
      storeTokens(frag.tokens.accessToken, frag.tokens.refreshToken);
      scheduleNextRefresh(frag.tokens.accessToken);
      emit({ type: "login", accessToken: frag.tokens.accessToken });
      return true;
    }
    const stored = storageGet(STORAGE_KEY_ACCESS);
    if (stored && !isTokenExpired(stored)) {
      await reconcileIdentity();
      const reconciled = storageGet(STORAGE_KEY_ACCESS);
      if (reconciled && !isTokenExpired(reconciled)) {
        scheduleNextRefresh(reconciled);
        return true;
      }
    }
    if (storageGet(STORAGE_KEY_REFRESH)) {
      const newToken = await doRefresh();
      if (newToken) return true;
    }
    if (initOptions.requireAuth) {
      redirectToLogin();
      return false;
    }
    return false;
  },
  /** Synchronous read; may be stale within REFRESH_BUFFER_MS of expiry. */
  getAccessToken() {
    return storageGet(STORAGE_KEY_ACCESS);
  },
  /**
   * Returns a token guaranteed to be valid for at least REFRESH_BUFFER_MS,
   * refreshing silently if needed. Returns null if refresh is impossible.
   */
  async getValidAccessToken() {
    const token = storageGet(STORAGE_KEY_ACCESS);
    if (token && !isTokenExpired(token)) return token;
    return doRefresh();
  },
  getUser() {
    const raw = storageGet(STORAGE_KEY_USER);
    if (!raw) return null;
    try {
      return JSON.parse(raw);
    } catch {
      return null;
    }
  },
  getTenants() {
    const raw = storageGet(STORAGE_KEY_TENANTS);
    if (!raw) return [];
    try {
      return JSON.parse(raw);
    } catch {
      return [];
    }
  },
  getActiveTenantId() {
    return storageGet(STORAGE_KEY_ACTIVE_TENANT);
  },
  setActiveTenantId(tenantId) {
    storageSet(STORAGE_KEY_ACTIVE_TENANT, tenantId);
  },
  isAuthenticated() {
    const t = storageGet(STORAGE_KEY_ACCESS);
    return !!t && !isTokenExpired(t);
  },
  /**
   * Send the browser to login.galigali.in for this origin, returning here.
   *
   * init() already does this internally when requireAuth is set and no session
   * can be established, but that path is unreachable to a consumer: the URL is
   * built from initOptions (origin + return_url), which is module state. A UI
   * that finds itself rendered without a session — a redirect frame that has
   * not committed, or a login handoff that came back with an error — could
   * previously only show a dead end, because reconstructing the login URL at
   * the call site would duplicate the origin/return_url contract.
   *
   * Purely additive: nothing existing calls this, and it changes no behaviour
   * for callers that do not.
   */
  login() {
    redirectToLogin();
  },
  /**
   * Best-effort server-side revoke + clear local state + broadcast + leave for
   * the login service. Safe to call when there's no active session.
   *
   * FINISHES WITH A NAVIGATION TO /auth/logout, NOT A REDIRECT TO THE LOGIN PAGE.
   *
   * The POST below revokes this origin's refresh token, but it can never clear
   * the login service's own gg_session cookie. That cookie is SameSite=Lax and
   * every portal is cross-SITE from login.galigali.in — pdooh.ai,
   * media.pdooh.ai and iot-admin.galigali.live are all different registrable
   * domains — so the browser neither sends it on a fetch nor honours a
   * Set-Cookie that tries to clear it. credentials:'include' does not help;
   * Lax and third-party cookie policy both still apply.
   *
   * The visible symptom was that sign-out did nothing: tokens were revoked and
   * localStorage cleared, we redirected to the login page, and the page found
   * the surviving gg_session, skipped OTP and signed the user back in.
   *
   * Navigating to GET /auth/logout fixes it because a top-level navigation is
   * first-party at the destination: the cookie is sent, the clear is honoured,
   * and the service then bounces back to return_url. The POST is kept so this
   * origin's refresh token is revoked even if the navigation is interrupted.
   */
  async logout(opts = {}) {
    const accessToken = storageGet(STORAGE_KEY_ACCESS);
    const refreshToken = storageGet(STORAGE_KEY_REFRESH);
    if (refreshToken || opts.logoutAll) {
      try {
        await fetch(initOptions.loginUrl + "/auth/logout", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...accessToken ? { Authorization: "Bearer " + accessToken } : {}
          },
          body: JSON.stringify({ refreshToken, logoutAll: !!opts.logoutAll })
        });
      } catch {
      }
    }
    clearTokens();
    broadcast({ type: "logout" });
    emit({ type: "logout" });
    if (typeof window === "undefined") return;
    const u = new URL(initOptions.loginUrl + "/auth/logout");
    u.searchParams.set("return_url", buildLoginUrl());
    window.location.href = u.toString();
  },
  /**
   * Convenience for fetch wrappers: call this when you receive a 401
   * from a backend. Refreshes once; if that fails, returns null and
   * the consumer should bubble the error.
   */
  async handle401() {
    return doRefresh();
  },
  /**
   * Subscribe to lifecycle events (login / logout / refresh / refresh_failed).
   * Returns an unsubscribe function.
   */
  onAuthChange(listener) {
    eventListeners.add(listener);
    return () => {
      eventListeners.delete(listener);
    };
  }
};
var src_default = authClient;
export {
  authClient,
  src_default as default,
  isTokenExpired,
  parseJwtPayload
};
